import { useState, useEffect } from "react";
import { Flex, Checkbox, Select, Radio, Text, Button, List } from "@theorgchartdev/storybook";
import {
  commonSelectProps,
  BuildChartSelectOptions,
  BuildFieldSelectOptions,
  BuildUAGroupOptions,
  BuildAccessGroupOptions
} from "@/appComponents/Blocks/Modals/SettingsModal/AccountSettings/Authorization/AuthorizationUtils";
import { GetChartMetadata } from "@/Utils/Promises";
import { GroupAssignmentModal } from "./GroupAssignmentModal";
import { GetWSMgr } from "@/Utils/StateLib";
import { FieldSelectListProps } from "@/Utils/ComponentLib";
import { appstr } from "@coreapp/utils/appstr";
import { appstrp } from "@coreapp/utils/appstrp";
import { Cappstr } from "@coreapp/utils/Cappstr";
import { getFeatureAccess } from "@/Utils/Misc";
import { enumFeaturesList } from "@coreapp/constants";
import UpgradeCard from "@/appComponents/Blocks/Composites/UpgradePopover/UpgradeCard";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { UpgradeInfoCard } from "@/appComponents/Blocks/Composites/UpgradePopover/UpgradeInfoCard";

export const AutoAssignmentConfiguration = ({ accountConfig, handleUpdate }) => {
  commonSelectProps.placeholder = appstrp("Select", "...");

  const [selectedChartID, setSelectedChartID] = useState(null);
  const [selectedChart, SetSelectedChart] = useState(null);
  const [showGroupMap, setShowGroupMap] = useState(false);
  const { showPricingAndPackaging } = usePermissionCheck();

  const LoadSelectedChart = async chartID => {
    const { result } = await GetChartMetadata(chartID);
    SetSelectedChart(result);
  };

  useEffect(() => {
    if (autoAssignObj?.chart) {
      const chartID = autoAssignObj.chart;
      setSelectedChartID(chartID);
      LoadSelectedChart(chartID);
    }
  }, [accountConfig, selectedChartID]);

  const securityObj = accountConfig.security;
  const autoAssignObj = securityObj?.userAssignment || { enabled: false };
  const groupMappingObj = autoAssignObj?.groups || {};
  const chartsList = accountConfig.charts || [];

  // Legacy Options Check
  if (
    Object.keys(autoAssignObj).length &&
    autoAssignObj.enabled === undefined &&
    autoAssignObj.disabled === undefined
  ) {
    autoAssignObj.enabled = false;
  }

  const HandleUAUpdate = updatedUAConfig => {
    let updatedAccountConfig = { ...accountConfig };
    updatedAccountConfig = {
      ...updatedAccountConfig,
      security: { ...updatedAccountConfig.security, userAssignment: updatedUAConfig }
    };

    handleUpdate(updatedAccountConfig, "security");
  };

  const EnableUAAlert = () => {
    const wsMgr = GetWSMgr();
    const header = appstr("autoassignwarningheader");
    const message = appstr("autoassignwarningmessage");
    const confirmStr = appstr("Turn On");
    const denyStr = appstr("Cancel");
    wsMgr.CreateReactAlert(
      header,
      message,
      () => ToggleUAEnabled(),
      () => {},
      confirmStr,
      denyStr
    );
  };

  const ToggleUAEnabled = () => {
    let updatedUAConfig = { ...autoAssignObj };
    if (!updatedUAConfig.enabled) updatedUAConfig.enabled = false;
    updatedUAConfig.enabled = !updatedUAConfig.enabled;
    HandleUAUpdate(updatedUAConfig);
  };

  const HandleUASelect = (key, value) => {
    let updatedUAConfig = { ...autoAssignObj };
    updatedUAConfig[key] = value;
    HandleUAUpdate(updatedUAConfig);
  };

  const ToggleRecRetrievalField = value => {
    const recordValue = value === 0 ? "username" : "email";
    let updatedUAConfig = { ...autoAssignObj };
    updatedUAConfig.userValue = recordValue;
    HandleUAUpdate(updatedUAConfig);
  };

  const ToggleUACaseInsensitive = () => {
    let updatedUAConfig = { ...autoAssignObj };
    updatedUAConfig.caseInsensitive = !updatedUAConfig.caseInsensitive;
    HandleUAUpdate(updatedUAConfig);
  };

  const DeleteUAGroupMapping = key => {
    let updatedGroupMapping = { ...groupMappingObj };
    delete updatedGroupMapping[key];
    const updatedUAConfig = { ...autoAssignObj, groups: updatedGroupMapping };
    HandleUAUpdate(updatedUAConfig);
  };

  const AddGroupMap = (key, value) => {
    let updatedUAConfig = { ...autoAssignObj };
    updatedUAConfig = { ...updatedUAConfig, groups: { ...updatedUAConfig.groups, [key]: value } };
    HandleUAUpdate(updatedUAConfig);
    if (showGroupMap) setShowGroupMap(false);
  };
  const ToggleDefaultGroupEnabled = () => {
    let updatedGroupMapping = { ...groupMappingObj };
    if ("_default" in updatedGroupMapping) DeleteUAGroupMapping("_default");
    else AddGroupMap("_default", "");
  };

  const groupAssignmentCheck =
    getFeatureAccess(enumFeaturesList.groupAutoAssignment) && showGroupMap;

  const autoAssignUpgradeDynamicText = {
    template: appstr("auto assign upgrade template"),
    formatConfig: { bold1: { type: "bold" } },
    replaceDict: { bold1: appstr("automatically assign") }
  };

  return (
    <Flex direction={"column"} gap={"8px"}>
      {showPricingAndPackaging && !getFeatureAccess(enumFeaturesList.groupAutoAssignment) && (
        <UpgradeCard
          iconName={"users-gear-regular"}
          featureKey={enumFeaturesList.groupAutoAssignment}
        >
          <UpgradeInfoCard.Body dynamic={autoAssignUpgradeDynamicText}>
            {appstr("auto assign upgrade message")}
          </UpgradeInfoCard.Body>
        </UpgradeCard>
      )}
      {getFeatureAccess(enumFeaturesList.groupAutoAssignment) && (
        <>
          <Checkbox
            key={"auth-ua-enable"}
            id={"auth-ua-enable"}
            data-testid="account-settings-authorization-autoassign-enable-checkbox"
            size={"medium"}
            label={appstr("Automatically assign users to access group upon login")}
            disabled={!getFeatureAccess(enumFeaturesList.groupAutoAssignment)}
            checked={autoAssignObj?.enabled && !autoAssignObj?.disabled}
            onToggle={() => {
              if (!autoAssignObj?.enabled) EnableUAAlert();
              else ToggleUAEnabled();
            }}
          />
          <Flex width={"400px"} gap={"8px"} direction={"column"}>
            <Select
              {...commonSelectProps}
              dataTestId="account-settings-authorization-autoassign-master-chart-select"
              label={appstr("Master chart used to validate user")}
              inputOnly={false}
              options={BuildChartSelectOptions(
                chartsList,
                "chart",
                HandleUASelect,
                "account-settings-authorization-autoassign-master-chart"
              )}
              value={autoAssignObj?.chart || null}
              disabled={!getFeatureAccess(enumFeaturesList.groupAutoAssignment)}
            />
            <Select
              {...commonSelectProps}
              dataTestId="account-settings-authorization-autoassign-field-select"
              disabled={
                selectedChart === null || !getFeatureAccess(enumFeaturesList.groupAutoAssignment)
              }
              label={appstr("Data field used for assignment")}
              inputOnly={false}
              listProps={FieldSelectListProps()}
              options={
                selectedChart?.headers
                  ? BuildFieldSelectOptions(
                      selectedChart.headers,
                      "field",
                      HandleUASelect,
                      "account-settings-authorization-autoassign-field"
                    )
                  : []
              }
              value={autoAssignObj?.field || null}
            />

            <Text size={"small"} modifier={"medium"}>
              {appstr("User Record Retrieval")}
            </Text>
            <Flex direction={"row"} gap={"8px"}>
              <Radio
                key={"auth-ret-radio1"}
                id={"auth-ret-radio1"}
                data-testid="account-settings-authorization-autoassign-retrieval-username-radio"
                checked={autoAssignObj?.userValue === "username"}
                value={0}
                onChange={() => ToggleRecRetrievalField(0)}
                label={appstr("userid")}
                disabled={!getFeatureAccess(enumFeaturesList.groupAutoAssignment)}
              />
              <Radio
                key={"auth-ret-radio2"}
                id={"auth-ret-radio2"}
                data-testid="account-settings-authorization-autoassign-retrieval-email-radio"
                checked={autoAssignObj?.userValue === "email"}
                value={1}
                onChange={() => ToggleRecRetrievalField(1)}
                label={appstr("Email")}
                disabled={!getFeatureAccess(enumFeaturesList.groupAutoAssignment)}
              />
              <Checkbox
                key={"auth-case-chkbx"}
                id={"auth-case-chkbx"}
                data-testid="account-settings-authorization-autoassign-case-insensitive-checkbox"
                size={"medium"}
                label={appstr("Case Insensitive Match")}
                checked={autoAssignObj?.caseInsensitive || false}
                onToggle={() => ToggleUACaseInsensitive()}
                disabled={!getFeatureAccess(enumFeaturesList.groupAutoAssignment)}
              />
            </Flex>

            <Select
              {...commonSelectProps}
              dataTestId="account-settings-authorization-autoassign-record-field-select"
              label={`${appstr("Field in Record")}:`}
              inputOnly={false}
              disabled={
                selectedChart === null || !getFeatureAccess(enumFeaturesList.groupAutoAssignment)
              }
              listProps={FieldSelectListProps()}
              options={
                selectedChart?.headers
                  ? BuildFieldSelectOptions(
                      selectedChart.headers,
                      "userField",
                      HandleUASelect,
                      "account-settings-authorization-autoassign-record-field"
                    )
                  : []
              }
              value={autoAssignObj?.userField || null}
            />
          </Flex>

          <Flex direction={"column"} gap={"8px"} width={"15%"}>
            <Text size={"small"} modifier={"medium"}>
              {appstr("datagroupmapping")}
            </Text>
            <Button
              modifier={"secondary"}
              className={"oc-special-secondary"}
              iconProps={{ name: "plus" }}
              disabled={
                !autoAssignObj?.field || !getFeatureAccess(enumFeaturesList.groupAutoAssignment)
              }
              onClick={() => setShowGroupMap(true)}
              dataTestId="account-settings-authorization-autoassign-create-mapping-btn"
            >
              {appstr("Create group mapping")}
            </Button>
          </Flex>
          <Flex direction={"row"} gap={"8px"} alignItems={"center"}>
            <Checkbox
              key={"auth-defgroup-chkbx"}
              id={"auth-defgroup-chkbx"}
              data-testid="account-settings-authorization-autoassign-default-group-checkbox"
              size="medium"
              label={appstr("Use default group instead of denying access")}
              checked={groupMappingObj && "_default" in groupMappingObj}
              onToggle={() => ToggleDefaultGroupEnabled()}
              disabled={!getFeatureAccess(enumFeaturesList.groupAutoAssignment)}
            />
            <Flex width={"15%"}>
              <Select
                {...commonSelectProps}
                dataTestId="account-settings-authorization-autoassign-default-group-select"
                options={BuildAccessGroupOptions(
                  securityObj.groups,
                  AddGroupMap,
                  null,
                  false,
                  "account-settings-authorization-autoassign-default-group"
                )}
                value={groupMappingObj?._default || null}
                disabled={!getFeatureAccess(enumFeaturesList.groupAutoAssignment)}
              />
            </Flex>
          </Flex>
        </>
      )}
      {getFeatureAccess(enumFeaturesList.groupAutoAssignment) && groupMappingObj && (
        <List
          enableVirtualization={false}
          options={BuildUAGroupOptions(groupMappingObj, securityObj.groups, DeleteUAGroupMapping)}
          listWidth="fill"
          optionSize={"medium"}
          showCheck={false}
          value={null}
        />
      )}
      {groupAssignmentCheck && (
        <GroupAssignmentModal
          show={showGroupMap}
          onClose={() => setShowGroupMap(false)}
          field={autoAssignObj.field}
          accessGroups={securityObj.groups}
          AddGroupMap={AddGroupMap}
        />
      )}
    </Flex>
  );
};
